import Vue from 'vue'
import { addNamespace } from './utils'
import { getProperty, hasProperty, snakeToCamelKeys } from '@/utils/object'
import {
  getTestResult,
  saveAnswer as saveTrainingAnswer,
  startFiveSharp,
  startTrialExam,
} from '@/apis/training'
import { saveAnswer as saveHomeworkAnswer } from '@/apis/homework'
import { UserAnswerModel } from '@/enums'

/**
 * @file
 * @author Thomas Grinderslev <thomas@edulab.dk>
 */

/**
 * @enum {string}
 */
export const QuestionState = {
  EMPTY: 'empty',
  CHANGED: 'changed',
  SAVING: 'saving',
  SAVED: 'saved',
}

/**
 * @typedef {{description: string, name: string, passed: boolean, pictureId: number|null}} ResultGrade
 * @typedef {{lessonId: number, lessonName: string, topicName: string, topicId: number, has5sharp: boolean, incorrect: number}} LessonScore
 * @typedef {{sessions: Object<number, Object<number, {resultErrorMessage: string, resultGrade: ResultGrade, resultLessonScores: Array<LessonScore>, questionErrorMessages: Object<number, string>, questionIds: [], questions: Object<number, Object>, questionStates: Object<number, QuestionState>, userAnswers: Object<number, Object>}>>}} QuestionStoreState
 */

/**
 * Prepares the given state object for a session of the given user answer model and session ID.
 *
 * @param {QuestionStoreState} state
 * @param {UserAnswerModel} userAnswerModel
 * @param {number} sessionId
 */
function prepareStateForSession(state, userAnswerModel, sessionId) {
  // Create an entry for the user answer model if it isn't there already
  if (!state.sessions[userAnswerModel]) {
    Vue.set(state.sessions, userAnswerModel, {})
  }

  // Create an entry for the session ID if it isn't there already
  if (!state.sessions[userAnswerModel][sessionId]) {
    Vue.set(state.sessions[userAnswerModel], sessionId, {})
  }
}

/**
 * The types used in this store
 * @enum {string}
 */
export const QuestionStoreTypes = {
  getters: {
    GET_QUESTION: 'getQuestion',
    GET_QUESTION_ERROR_MESSAGE: 'getQuestionErrorMessage',
    GET_QUESTION_STATE: 'getQuestionState',
    GET_QUESTIONS: 'getQuestions',
    GET_ANSWERED_QUESTIONS_COUNT: 'getAnsweredQuestionsCount',
    GET_ANSWER_STATISTICS: 'getAnswerStatistics',
    GET_USER_ANSWER: 'getUserAnswer',
    GET_QUESTION_STATE_COUNT: 'getQuestionStateCount',
    GET_RESULT_ERROR_MESSAGE: 'getResultErrorMessage',
    GET_RESULT_GRADE: 'getResultGrade',
    GET_RESULT_LESSON_SCORES: 'getResultLessonScores',
    HAS_SESSION_DATA: 'hasSessionData',
  },
  actions: {
    FETCH_RESULT: 'fetchResult',
    INITIALIZE_FIVE_SHARP: 'initializeFiveSharp',
    INITIALIZE_TRIAL_EXAM: 'initializeTrialExam',
    INITIALIZE_SESSION: 'initializeSession',
    SELECT_USER_ANSWER: 'selectUserAnswer',
    SUBMIT_USER_ANSWER: 'submitUserAnswer',
  },
  mutations: {
    SET_SESSION: 'setSession',
    SET_QUESTION: 'setQuestion',
    EXTEND_QUESTION: 'extendQuestion',
    CLEAR_QUESTION_ERROR_MESSAGE: 'clearQuestionErrorMessage',
    SET_QUESTION_ERROR_MESSAGE: 'setQuestionErrorMessage',
    SET_QUESTION_STATE: 'setQuestionState',
    SET_RESULT_ERROR_MESSAGE: 'setResultErrorMessage',
    SET_RESULT_GRADE: 'setResultGrade',
    SET_RESULT_LESSON_SCORES: 'setResultLessonScores',
    SET_USER_ANSWER: 'setUserAnswer',
  },
}

/**
 * A namespaced version of the types used in this store
 * @enum {string}
 */
export const QuestionStoreNamespacedTypes = addNamespace(
  'questions',
  QuestionStoreTypes
)

/**
 * @returns {QuestionStoreState}
 */
export function state() {
  return { sessions: {} }
}

export const getters = {
  /**
   * @param {QuestionStoreState} state
   * @returns {function(UserAnswerModel, number, number):Object}
   */
  [QuestionStoreTypes.getters.GET_QUESTION]: state => (
    userAnswerModel,
    sessionId,
    questionId
  ) => {
    return getProperty(
      state.sessions,
      `${userAnswerModel}.${sessionId}.questions.${questionId}`,
      null
    )
  },

  /**
   * @param {QuestionStoreState} state
   * @returns {function(UserAnswerModel, number, number):String}
   */
  [QuestionStoreTypes.getters.GET_QUESTION_ERROR_MESSAGE]: state => (
    userAnswerModel,
    sessionId,
    questionId
  ) => {
    return getProperty(
      state.sessions,
      `${userAnswerModel}.${sessionId}.questionErrorMessages.${questionId}`,
      ''
    )
  },

  /**
   * @param {QuestionStoreState} state
   * @returns {function(UserAnswerModel, number, number):QuestionState}
   */
  [QuestionStoreTypes.getters.GET_QUESTION_STATE]: state => (
    userAnswerModel,
    sessionId,
    questionId
  ) => {
    return getProperty(
      state.sessions,
      `${userAnswerModel}.${sessionId}.questionStates.${questionId}`,
      QuestionState.EMPTY
    )
  },

  /**
   * @param {QuestionStoreState} state
   * @returns {function(UserAnswerModel, number):Array}
   */
  [QuestionStoreTypes.getters.GET_QUESTIONS]: (state, getters) => (
    userAnswerModel,
    sessionId
  ) => {
    return getProperty(
      state.sessions,
      `${userAnswerModel}.${sessionId}.questionIds`,
      []
    ).map(questionId =>
      getters[QuestionStoreTypes.getters.GET_QUESTION](
        userAnswerModel,
        sessionId,
        questionId
      )
    )
  },

  /**
   * @param {QuestionStoreState} state
   * @returns {function(UserAnswerModel, number, number):Object}
   */
  [QuestionStoreTypes.getters.GET_USER_ANSWER]: state => (
    userAnswerModel,
    sessionId,
    id
  ) => {
    return (
      getProperty(
        state.sessions,
        `${userAnswerModel}.${sessionId}.userAnswers.${id}`
      ) || null
    )
  },

  /**
   * Returns the count of all questions in the given session with the given state.
   *
   * @param {QuestionStoreState} state
   * @returns {function(UserAnswerModel, number, QuestionState):Object}
   */
  [QuestionStoreTypes.getters.GET_QUESTION_STATE_COUNT]: state => (
    userAnswerModel,
    sessionId,
    questionState
  ) => {
    return Object.values(
      getProperty(
        state.sessions,
        `${userAnswerModel}.${sessionId}.questionStates`,
        {}
      )
    ).filter(state => state === questionState).length
  },

  /**
   *
   * @param {QuestionStoreState} state
   * @returns {function(UserAnswerModel, number):Object}
   */
  [QuestionStoreTypes.getters.GET_ANSWER_STATISTICS]: (state, getters) => (
    userAnswerModel,
    sessionId
  ) => {
    const statistics = {
      answers: {
        possible: 0,
        correct: 0,
        wrong: 0,
        unanswered: 0,
      },
      points: {
        possible: 0,
        achieved: 0,
      },
    }

    getters[QuestionStoreTypes.getters.GET_QUESTIONS](
      userAnswerModel,
      sessionId
    ).forEach(question => {
      statistics.answers.possible++
      statistics.points.possible += question.Question.point

      if (!question.UserAnswer) {
        statistics.answers.unanswered++
      } else {
        if (question.UserAnswer.correct) {
          statistics.answers.correct++
          statistics.points.achieved += question.Question.point
        } else {
          statistics.answers.wrong++
        }
      }
    })

    return statistics
  },

  /**
   * @param {QuestionStoreState} state
   * @returns {function(UserAnswerModel, number):string}
   */
  [QuestionStoreTypes.getters.GET_RESULT_ERROR_MESSAGE]: state => (
    userAnswerModel,
    sessionId
  ) => {
    return getProperty(
      state.sessions,
      `${userAnswerModel}.${sessionId}.resultErrorMessage`,
      ''
    )
  },

  /**
   * @param {QuestionStoreState} state
   * @returns {function(UserAnswerModel, number):ResultGrade}
   */
  [QuestionStoreTypes.getters.GET_RESULT_GRADE]: state => (
    userAnswerModel,
    sessionId
  ) => {
    return getProperty(
      state.sessions,
      `${userAnswerModel}.${sessionId}.resultGrade`,
      null
    )
  },

  /**
   * @param {QuestionStoreState} state
   * @returns {function(UserAnswerModel, number):string}
   */
  [QuestionStoreTypes.getters.GET_RESULT_LESSON_SCORES]: state => (
    userAnswerModel,
    sessionId
  ) => {
    return getProperty(
      state.sessions,
      `${userAnswerModel}.${sessionId}.lessonScores`,
      null
    )
  },

  /**
   * @param {QuestionStoreState} state
   * @returns {function(UserAnswerModel, number):boolean}
   */
  [QuestionStoreTypes.getters.HAS_SESSION_DATA]: state => (
    userAnswerModel,
    sessionId
  ) => {
    return hasProperty(state.sessions, `${userAnswerModel}.${sessionId}`)
  },
}

export const actions = {
  /**
   * Calls the server for Five Sharp test data and initializes the store with the returned session data.
   *
   * @param {VuexCommit} commit
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} lessonId
   * @param {number} classLevel
   * @return {Promise<number>}
   */
  [QuestionStoreTypes.actions.INITIALIZE_FIVE_SHARP](
    { commit },
    { lessonId, classLevel }
  ) {
    return startFiveSharp(lessonId, classLevel).then(
      ({ questions, testSettings }) => {
        const sessionId = parseInt(testSettings.Test.id)

        commit(QuestionStoreTypes.mutations.SET_SESSION, {
          sessionId,
          userAnswerModel: UserAnswerModel.TEST5SHARP,
          questions,
        })

        return sessionId
      }
    )
  },
  /**
   * Calls the server for trial exam data and initializes the store with the returned session data.
   *
   * @param {VuexCommit} commit
   * @param {UserAnswerModel} userAnswerModel
   * @param {TrialExamPostData} postData
   * @return {Promise<number>}
   */
  [QuestionStoreTypes.actions.INITIALIZE_TRIAL_EXAM]({ commit }, postData) {
    return startTrialExam(postData).then(({ questions, testSettings }) => {
      const sessionId = parseInt(testSettings.Test.id)

      commit(QuestionStoreTypes.mutations.SET_SESSION, {
        sessionId,
        userAnswerModel: UserAnswerModel.TESTTRIALEXAM,
        questions,
      })

      return sessionId
    })
  },

  /**
   * Initializes the store with the given session data.
   *
   * @param {VuexCommit} commit
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {Array} questions
   */ [QuestionStoreTypes.actions.INITIALIZE_SESSION](
    { commit },
    { sessionId, userAnswerModel, questions }
  ) {
    commit(QuestionStoreTypes.mutations.SET_SESSION, {
      sessionId,
      userAnswerModel,
      questions,
    })
  },

  /**
   * Fetches the result for the given session data.
   *
   * @param {VuexCommit} commit
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {Array} questions
   */
  [QuestionStoreTypes.actions.FETCH_RESULT](
    { commit, getters },
    { sessionId, userAnswerModel }
  ) {
    switch (userAnswerModel) {
      case UserAnswerModel.TEST5SHARP:
      case UserAnswerModel.TESTSTANDARD:
      case UserAnswerModel.TESTTRIALEXAM:
        return getTestResult(sessionId)
          .then(({ lessonScores, questions, statistics: { grade } }) => {
            if (
              getters[QuestionStoreTypes.getters.HAS_SESSION_DATA]({
                sessionId,
                userAnswerModel,
              })
            ) {
              // Extend questions if data for the session already exists in the store
              questions.forEach(question => {
                commit(QuestionStoreTypes.mutations.EXTEND_QUESTION, {
                  userAnswerModel,
                  sessionId,
                  question,
                  questionId: question.Question.id,
                })
              })
            } else {
              // Set up a new session with the result data if no session data exists yet
              commit(QuestionStoreTypes.mutations.SET_SESSION, {
                sessionId,
                userAnswerModel,
                questions,
              })
            }

            commit(QuestionStoreTypes.mutations.SET_RESULT_GRADE, {
              userAnswerModel,
              sessionId,
              grade: snakeToCamelKeys(grade),
            })

            commit(QuestionStoreTypes.mutations.SET_RESULT_LESSON_SCORES, {
              userAnswerModel,
              sessionId,
              lessonScores: Object.keys(lessonScores).map(key => {
                const lessonScore = lessonScores[key]

                return {
                  lessonId: parseInt(key),
                  lessonName: lessonScore.name,
                  topicId: parseInt(lessonScore.topic_id),
                  topicName: lessonScore.topic,
                  has5sharp: lessonScore.has5sharp,
                  incorrect: lessonScore.incorrect,
                }
              }),
            })
          })
          .catch(({ message }) => {
            commit(QuestionStoreTypes.mutations.SET_RESULT_ERROR_MESSAGE, {
              userAnswerModel,
              sessionId,
              message,
            })
          })
      default:
        throw 'Unknown user answer model: ' + userAnswerModel
    }
  },

  /**
   * Saves the given user answer to the store, but doesn't submit it to the server.
   *
   * @param {VuexCommit} commit
   * @param {VuexGetters} getters
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {number} questionId
   * @param {number|string} userAnswer
   * @param {number} lapsedTime
   */
  [QuestionStoreTypes.actions.SELECT_USER_ANSWER](
    { commit },
    { userAnswerModel, sessionId, questionId, userAnswer, lapsedTime }
  ) {
    commit(QuestionStoreTypes.mutations.SET_USER_ANSWER, {
      userAnswerModel,
      sessionId,
      questionId,
      userAnswer,
      lapsedTime,
    })

    commit(QuestionStoreTypes.mutations.SET_QUESTION_STATE, {
      userAnswerModel,
      sessionId,
      questionId,
      questionState:
        userAnswer || userAnswer === 0 || userAnswer === '0'
          ? QuestionState.CHANGED
          : QuestionState.EMPTY,
    })
  },

  /**
   * Submits a user answer to the server and updates the store with the user answer, server response or any error messages.
   * If no user answer is given both user answer and lapsed time will be loaded from the store if possible.
   *
   * @param {VuexCommit} commit
   * @param {VuexGetters} getters
   * @param {number} questionId
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {*=} userAnswer
   * @param {number=} lapsedTime
   * @returns {Promise} A promise that resolves with the server response.
   */
  async [QuestionStoreTypes.actions.SUBMIT_USER_ANSWER](
    { commit, getters },
    { userAnswerModel, sessionId, questionId, userAnswer, lapsedTime }
  ) {
    // Try to find a user answer in the store if no user answer is given
    if (userAnswer === undefined) {
      try {
        const storedAnswer = getters[
          QuestionStoreTypes.getters.GET_USER_ANSWER
        ](userAnswerModel, sessionId, questionId)

        userAnswer = storedAnswer.userAnswer
        lapsedTime = storedAnswer.lapsedTime
      } catch (err) {
        // Do nothing
      }
    }

    commit(QuestionStoreTypes.mutations.SET_USER_ANSWER, {
      userAnswerModel,
      sessionId,
      questionId,
      userAnswer,
      lapsedTime,
    })

    commit(QuestionStoreTypes.mutations.CLEAR_QUESTION_ERROR_MESSAGE, {
      userAnswerModel,
      sessionId,
      questionId,
    })

    commit(QuestionStoreTypes.mutations.SET_QUESTION_STATE, {
      userAnswerModel,
      sessionId,
      questionId,
      questionState: QuestionState.SAVING,
    })

    // Wait for the defined time before sending anything to the server, to ease the stress on the server in stressful situations
    if (window.vueSettings && window.vueSettings.answerDelay) {
      await new Promise(resolve =>
        setTimeout(() => resolve(), window.vueSettings.answerDelay)
      )
    }

    let saveCallback

    switch (userAnswerModel) {
      case UserAnswerModel.TEST5SHARP:
      case UserAnswerModel.TESTSTANDARD:
      case UserAnswerModel.TESTTRIALEXAM:
        saveCallback = saveTrainingAnswer(
          questionId,
          userAnswer,
          lapsedTime,
          sessionId,
          userAnswerModel
        )
        break
      case UserAnswerModel.STANDARD:
      case UserAnswerModel.DIFFERENTIATED:
        saveCallback = saveHomeworkAnswer(
          questionId,
          userAnswer,
          lapsedTime,
          sessionId
        )
        break
      default:
        commit(QuestionStoreTypes.mutations.SET_QUESTION_ERROR_MESSAGE, {
          userAnswerModel,
          sessionId,
          questionId,
          errorMessage: `Unknown user answer model: "${userAnswerModel}"`,
        })
        commit(QuestionStoreTypes.mutations.SET_QUESTION_STATE, {
          userAnswerModel,
          sessionId,
          questionId,
          questionState: QuestionState.CHANGED,
        })
        return
    }

    saveCallback
      .then(question => {
        commit(QuestionStoreTypes.mutations.EXTEND_QUESTION, {
          userAnswerModel,
          sessionId,
          questionId,
          question,
        })

        commit(QuestionStoreTypes.mutations.SET_QUESTION_STATE, {
          userAnswerModel,
          sessionId,
          questionId,
          questionState: QuestionState.SAVED,
        })
      })
      .catch(error => {
        commit(QuestionStoreTypes.mutations.SET_QUESTION_ERROR_MESSAGE, {
          userAnswerModel,
          sessionId,
          questionId,
          errorMessage: error.message || error,
        })
        commit(QuestionStoreTypes.mutations.SET_QUESTION_STATE, {
          userAnswerModel,
          sessionId,
          questionId,
          questionState: QuestionState.CHANGED,
        })
      })

    return saveCallback
  },
}

export const mutations = {
  /**
   * Sets up a session with the given questions.
   * The question array is traversed and the necessary arrays and maps are added to the state.
   *
   * @param {QuestionStoreState} state
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {Array} questions
   * @param {Object=} userAnswers
   */
  [QuestionStoreTypes.mutations.SET_SESSION](
    state,
    { userAnswerModel, sessionId, questions, userAnswers = {} }
  ) {
    prepareStateForSession(state, userAnswerModel, sessionId)

    const questionMap = {}
    const questionStates = {}

    // Create an array of question IDs while also adding data to ID keyed objects for easier future access
    const questionIds = questions.map(question => {
      const questionId = getProperty(question, 'Question.id')

      questionMap[questionId] = question

      // Identify earlier answered questions
      if (question.UserAnswer && question.UserAnswer.id) {
        questionStates[questionId] = QuestionState.SAVED
      } else {
        questionStates[questionId] = QuestionState.EMPTY
      }

      return questionId
    })

    Vue.set(state.sessions[userAnswerModel], sessionId, {
      questionIds,
      questions: questionMap,
      questionStates,
      userAnswers,
      questionErrorMessages: {},
    })
  },

  /**
   * @param {QuestionStoreState} state
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {number} questionId
   * @param {Object} question
   */
  [QuestionStoreTypes.mutations.SET_QUESTION](
    state,
    { userAnswerModel, sessionId, questionId, question }
  ) {
    Vue.set(
      state.sessions[userAnswerModel][sessionId].questions,
      questionId,
      question
    )
  },

  /**
   * Extends any existing question with the given data.
   *
   * @param {QuestionStoreState} state
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {number} questionId
   * @param {Object} question Question related data to extend with.
   */
  [QuestionStoreTypes.mutations.EXTEND_QUESTION](
    state,
    { userAnswerModel, sessionId, questionId, question }
  ) {
    Vue.set(
      state.sessions[userAnswerModel][sessionId].questions,
      questionId,
      Object.assign(
        {},
        state.sessions[userAnswerModel][sessionId].questions[questionId],
        question
      )
    )
  },

  /**
   * @param {QuestionStoreState} state
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {Array} questions
   */
  [QuestionStoreTypes.mutations.CLEAR_QUESTION_ERROR_MESSAGE](
    state,
    { userAnswerModel, sessionId, questionId }
  ) {
    Vue.delete(
      state.sessions[userAnswerModel][sessionId].questionErrorMessages,
      questionId
    )
  },

  /**
   * @param {QuestionStoreState} state
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {Array} questions
   */
  [QuestionStoreTypes.mutations.SET_QUESTION_ERROR_MESSAGE](
    state,
    { userAnswerModel, sessionId, questionId, errorMessage }
  ) {
    Vue.set(
      state.sessions[userAnswerModel][sessionId].questionErrorMessages,
      questionId,
      errorMessage
    )
  },

  /**
   * @param {QuestionStoreState} state
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {Array} questions
   */
  [QuestionStoreTypes.mutations.SET_QUESTION_STATE](
    state,
    { userAnswerModel, sessionId, questionId, questionState }
  ) {
    Vue.set(
      state.sessions[userAnswerModel][sessionId].questionStates,
      questionId,
      questionState
    )
  },

  /**
   * @param {QuestionStoreState} state
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {string} errorMessage
   */
  [QuestionStoreTypes.mutations.SET_RESULT_ERROR_MESSAGE](
    state,
    { userAnswerModel, sessionId, errorMessage }
  ) {
    prepareStateForSession(state, userAnswerModel, sessionId)

    state.sessions[userAnswerModel][sessionId].resultErrorMessage = errorMessage
  },

  /**
   * @param {QuestionStoreState} state
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {ResultGrade} grade
   */
  [QuestionStoreTypes.mutations.SET_RESULT_GRADE](
    state,
    { userAnswerModel, sessionId, grade }
  ) {
    prepareStateForSession(state, userAnswerModel, sessionId)

    state.sessions[userAnswerModel][sessionId].resultGrade = grade
  },
  /**
   * @param {QuestionStoreState} state
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {Array<{lessonId: number, has5sharp: boolean, incorrect: number, name: string, topicName: string, topicId: number}>} lessonScores
   */
  [QuestionStoreTypes.mutations.SET_RESULT_LESSON_SCORES](
    state,
    { userAnswerModel, sessionId, lessonScores }
  ) {
    prepareStateForSession(state, userAnswerModel, sessionId)

    state.sessions[userAnswerModel][sessionId].lessonScores = lessonScores
  },

  /**
   * @param {QuestionStoreState} state
   * @param {UserAnswerModel} userAnswerModel
   * @param {number} sessionId
   * @param {Array} questions
   */
  [QuestionStoreTypes.mutations.SET_USER_ANSWER](
    state,
    { userAnswerModel, sessionId, questionId, userAnswer, lapsedTime }
  ) {
    Vue.set(
      state.sessions[userAnswerModel][sessionId].userAnswers,
      questionId,
      {
        userAnswer,
        lapsedTime,
      }
    )
  },
}

export default {
  namespaced: true,
  Types: QuestionStoreTypes,
  NamespacedTypes: QuestionStoreNamespacedTypes,
  state,
  getters,
  actions,
  mutations,
}
